import type { ImageBleed } from 'types';
import type { Affiliate, Price as PriceType } from 'types/capi/shared';

import ChevronIcon from '~/assets/icons/Chevron';
import ExternalIcon from '~/assets/icons/ExternalIcon';
import Price from '~/components/Price';

export type LinkType = 'product' | 'article' | 'plp' | 'external';

export default function ImageMetadata({
  affiliate,
  bleed,
  linkType,
  price,
  title,
  variantCount
}: {
  affiliate?: Affiliate;
  bleed?: ImageBleed;
  linkType?: LinkType;
  price?: PriceType;
  title: string;
  variantCount?: number;
}) {
  function VariantCount({ className }: { className: string }) {
    if (!variantCount) return <></>;
    if (variantCount < 2) return <></>;

    return (
      <span
        className={`${className} items-center gap-2 text-sm font-light text-grey-hide`}
      >
        <span className="hidden xs:inline">•</span>
        <span>{`${variantCount} colour${variantCount === 1 ? '' : 's'}`}</span>
      </span>
    );
  }

  const bleedPadding =
    bleed === 'full'
      ? 'flex w-full justify-center px-4 md:px-10'
      : bleed === 'left'
        ? 'max-md:px-4 md:pl-8 desktop:pl-0'
        : bleed === 'right'
          ? 'max-md:px-4 md:pr-8 desktop:pr-0'
          : '';

  return (
    <div className={`w-full ${bleedPadding}`}>
      <div
        className={`flex w-full flex-col gap-1.5 md:min-h-[61px] ${
          bleed === 'left' ? 'md:items-end' : ''
        } ${bleed === 'full' ? 'max-w-desktop' : ''}`}
      >
        <div className="flex items-center gap-1">
          {title && (
            <h4
              className={`line-clamp-2 font-heading text-sm font-medium leading-tight ${
                bleed === 'left' ? 'md:text-right' : 'md:text-left'
              }`}
            >
              {title}
            </h4>
          )}
        </div>

        <div
          className={`flex flex-row items-center gap-2 max-md:flex-wrap max-md:gap-y-1 ${
            bleed === 'left' ? 'md:justify-end' : ''
          }`}
        >
          {price && (
            <Price
              regular={price.regular}
              sale={price.sale}
              currency={price.currency}
              className="gap-2 text-sm"
            />
          )}
          {affiliate && (
            <div className="flex flex-row items-center gap-1">
              <ExternalIcon />
              <h4 className="text-caption-grey">{affiliate.name}</h4>
            </div>
          )}
          {!affiliate && ['plp', 'article'].includes(linkType ?? '') && (
            <div className="flex items-baseline gap-2">
              <p className="text-caption-grey">
                {linkType === 'plp'
                  ? 'Shop selection'
                  : linkType === 'article'
                    ? 'View article'
                    : ''}
              </p>
              <div className="rotate-270">
                <ChevronIcon stroke="#A9A9A9" width="10" height="10" />
              </div>
            </div>
          )}
          <VariantCount className="hidden xs:flex" />
        </div>
        <VariantCount className="xs:hidden" />
      </div>
    </div>
  );
}
